import { createContext, ReactNode, useContext, useMemo } from 'react'

/**
 * Unified Platform/Team Context
 *
 * Single source of truth for platform/team mode across the entire application.
 * Replaces fragmented context systems (DeployContext, InstanceContext).
 *
 * Injected at Layout level (TeamLayout / Layout) and available to all child components.
 *
 * Note: This is separate from the legacy AppContext (refresh callback).
 * TODO: Merge with AppContext in Phase 2 after validation.
 */
export interface UnifiedContextType {
  /**
   * Current application mode
   * - 'platform': Personal/Platform view (routes under Layout)
   * - 'team': Team view (routes under TeamLayout)
   */
  mode: 'platform' | 'team'

  /**
   * Team resource ID (required when mode is 'team')
   * Source: teamStore.teamTokenData?.teamResID
   */
  teamResID?: string

  /**
   * Member resource ID (required when mode is 'team')
   * Default: teamStore.scopeInfo?.resID (current logged-in user)
   * Override: URL param from /team/member/:id (when admin views specific member)
   */
  memberResID?: string
}

export const UnifiedContext = createContext<UnifiedContextType | null>(null)

/**
 * Unified Context Provider
 *
 * Should be used in Layout components to provide context to all child routes.
 *
 * @example
 * // In TeamLayout
 * <UnifiedContextProvider
 *   mode="team"
 *   teamResID={teamStore.teamTokenData?.teamResID}
 *   memberResID={teamStore.scopeInfo?.resID}
 * >
 *   <Outlet />
 * </UnifiedContextProvider>
 *
 * @example
 * // In Layout
 * <UnifiedContextProvider mode="platform">
 *   <Outlet />
 * </UnifiedContextProvider>
 */
export function UnifiedContextProvider({
  children,
  mode,
  teamResID,
  memberResID,
}: UnifiedContextType & { children: ReactNode }) {
  const value = useMemo(
    () => ({
      mode,
      teamResID,
      memberResID,
    }),
    [mode, teamResID, memberResID],
  )

  return <UnifiedContext.Provider value={value}>{children}</UnifiedContext.Provider>
}

/**
 * Hook to consume Unified Context
 *
 * Throws error if used outside of UnifiedContextProvider.
 * All components should use this hook to access context.
 *
 * @returns Unified context (guaranteed non-null)
 * @throws Error if used outside of UnifiedContextProvider
 *
 * @example
 * function MyComponent() {
 *   const { mode } = useUnifiedContext()
 *   if (mode === 'team') {
 *     // Team-specific logic
 *   }
 * }
 */
export function useUnifiedContext(): UnifiedContextType {
  const ctx = useContext(UnifiedContext)
  if (!ctx) {
    throw new Error('useUnifiedContext must be used within UnifiedContextProvider')
  }
  return ctx
}

/**
 * Context Override
 *
 * Used to override context fields in specific routes.
 * Creates a nested context that inherits parent but overrides specified fields.
 *
 * @example
 * // Override memberResID: /team/member/:id (admin viewing specific member)
 * function MemberDetailPage() {
 *   const { id } = useParams()
 *   return (
 *     <UnifiedContextOverride memberResID={id}>
 *       <MemberContent />
 *     </UnifiedContextOverride>
 *   )
 * }
 */
export function UnifiedContextOverride({
  mode,
  memberResID,
  children,
}: {
  mode?: 'platform' | 'team'
  memberResID?: string
  children: ReactNode
}) {
  const parent = useUnifiedContext()
  const value = useMemo(
    () => ({
      ...parent,
      ...(mode !== undefined && { mode }),
      ...(memberResID !== undefined && { memberResID }),
    }),
    [parent, mode, memberResID],
  )

  return <UnifiedContext.Provider value={value}>{children}</UnifiedContext.Provider>
}

/**
 * Helper: Check if context is in team mode
 */
export function isTeamMode(ctx: UnifiedContextType): boolean {
  return ctx.mode === 'team'
}

/**
 * Helper: Check if context is in platform mode
 */
export function isPlatformMode(ctx: UnifiedContextType): boolean {
  return ctx.mode === 'platform'
}

/**
 * Helper: Get base path for current mode
 * - Platform: ''
 * - Team: '/team'
 */
export function getBasePath(ctx: UnifiedContextType): string {
  return ctx.mode === 'team' ? '/team' : ''
}

/**
 * Helper: Get instance list page path
 * - Platform: '/instances'
 * - Team: '/team/instances/member'
 */
export function getInstanceListPath(ctx: UnifiedContextType): string {
  return ctx.mode === 'team' ? `${getBasePath(ctx)}/instances/member` : '/instances'
}

export function getInstanceListExpandPath(ctx: UnifiedContextType, instanceResId: string): string {
  return `${getInstanceListPath(ctx)}?expand=${encodeURIComponent(instanceResId)}`
}

/**
 * Helper: Get deployment list page path
 * - Platform: '/deploy'
 * - Team: '/team/deploy'
 */
export function getDeployListPath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/deploy`
}

/**
 * Helper: Get deployment creation page path
 * - Platform: '/deploy/create'
 * - Team: '/team/deploy/create'
 */
export function getDeployCreatePath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/deploy/create`
}

/**
 * Helper: Get scaling list page path
 * - Platform: '/scaling'
 * - Team: '/team/scaling'
 */
export function getScaleListPath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/scaling`
}

/**
 * Helper: Get scaling creation page path
 * - Platform: '/scaling/create'
 * - Team: '/team/scaling/create'
 */
export function getScaleCreatePath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/scaling/create`
}

/**
 * Helper: Get scaling monitor page path
 * - Platform: '/scaling/monitor'
 * - Team: '/team/scaling/monitor'
 */
export function getScaleMonitorPath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/scaling/monitor`
}

/**
 * Helper: Get EIP list page path
 * - Platform: '/eip'
 * - Team: '/team/eip'
 */
export function getEIPListPath(ctx: UnifiedContextType): string {
  return `${getBasePath(ctx)}/eip`
}

/**
 * Helper: Get EIP ACL rules page path
 * - Platform: '/eip/:eipResId/acl'
 * - Team: '/team/eip/:eipResId/acl'
 */
export function getEIPACLPath(ctx: UnifiedContextType, eipResId: string): string {
  return `${getEIPListPath(ctx)}/${eipResId}/acl`
}

export function getEIPL3RuleCreatePath(ctx: UnifiedContextType, eipResId: string): string {
  return `${getEIPACLPath(ctx, eipResId)}/l3/new`
}

export function getEIPL3RuleEditPath(
  ctx: UnifiedContextType,
  eipResId: string,
  l3RuleId: number | string,
): string {
  return `${getEIPACLPath(ctx, eipResId)}/l3/${l3RuleId}/edit`
}

export function getEIPL4RuleCreatePath(
  ctx: UnifiedContextType,
  eipResId: string,
  l3RuleId: number | string,
): string {
  return `${getEIPACLPath(ctx, eipResId)}/l3/${l3RuleId}/l4/new`
}

export function getEIPL4RuleEditPath(
  ctx: UnifiedContextType,
  eipResId: string,
  l3RuleId: number | string,
  l4RuleId: number | string,
): string {
  return `${getEIPACLPath(ctx, eipResId)}/l3/${l3RuleId}/l4/${l4RuleId}/edit`
}

/**
 * Helper: Validate team context has required fields
 */
export function isValidTeamContext(ctx: UnifiedContextType): ctx is Required<UnifiedContextType> {
  return isTeamMode(ctx) && !!ctx.teamResID && !!ctx.memberResID
}

/**
 * Helper: Get team context or throw error
 * Use this when you need guaranteed team context values
 */
export function requireTeamContext(ctx: UnifiedContextType): Required<UnifiedContextType> {
  if (!isValidTeamContext(ctx)) {
    throw new Error(
      `Team context required but invalid. mode=${ctx.mode}, teamResID=${ctx.teamResID}, memberResID=${ctx.memberResID}`,
    )
  }
  return ctx
}
